import { Component, Input, OnInit } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ReportFilterState, toApiParams } from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';

@Component({
  selector: 'app-financial-dashboard',
  templateUrl: './financial-dashboard.component.html',
  styleUrls: ['./financial-dashboard.component.scss']
})
export class FinancialDashboardComponent implements OnInit {

  @Input() storeId: string = '';

  currentBalance: any = null;
  payoutEstimate: any = null;
  balanceHistory: any[] = [];
  transactions: any[] = [];
  filteredTotals: any = null;

  filterState: ReportFilterState = { period: 'this_month' };
  transactionType: string = '';
  paymentStatus: string = '';
  paymentMethod: string = '';
  currentPage: number = 1;
  totalPages: number = 1;
  totalTransactions: number = 0;
  pageSize: number = 20;

  loadingBalance: boolean = false;
  loadingBalanceHistory: boolean = false;
  loadingTransactions: boolean = false;
  private balanceLoadSeq = 0;
  private balanceHistoryLoadSeq = 0;
  private transactionsLoadSeq = 0;

  transactionTypes = [
    { value: '', labelKey: 'KPI.FINANCIAL.ALL_TYPES' },
    { value: 'sale', labelKey: 'KPI.FINANCIAL.BY_TYPE_SALE' },
    { value: 'refund', labelKey: 'KPI.FINANCIAL.BY_TYPE_REFUND' },
    { value: 'commission', labelKey: 'KPI.FINANCIAL.COMMISSION' },
    { value: 'payout', labelKey: 'KPI.FINANCIAL.PAYOUT_STATUS' },
    { value: 'adjustment', labelKey: 'KPI.FINANCIAL.BY_TYPE_ADJUSTMENT' },
    { value: 'fee', labelKey: 'KPI.FINANCIAL.BY_TYPE_FEE' }
  ];

  paymentStatuses = [
    { value: '', labelKey: 'KPI.FINANCIAL.ALL_STATUSES' },
    { value: 'completed', labelKey: 'KPI.FINANCIAL.YES' },
    { value: 'pending', labelKey: 'KPI.FINANCIAL.STATUS' },
    { value: 'failed', labelKey: 'KPI.FINANCIAL.NO' },
    { value: 'refunded', labelKey: 'KPI.FINANCIAL.BY_TYPE_REFUND' }
  ];

  paymentMethods = [
    { value: '', labelKey: 'KPI.FINANCIAL.ALL_METHODS' },
    { value: 'card', labelKey: 'KPI.FINANCIAL.PAYMENT_METHOD' },
    { value: 'mobilepay', labelKey: 'KPI.FINANCIAL.PAYMENT_METHOD' },
    { value: 'cash', labelKey: 'KPI.FINANCIAL.PAYMENT_METHOD' }
  ];

  Math = Math;

  constructor(
    private ecommerceService: EcommerceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    // Initial load triggered by report-filter-bar emit on init
  }

  get filtersDisabled(): boolean {
    return this.loadingBalance || this.loadingBalanceHistory || this.loadingTransactions;
  }

  onFilterChange(filter: ReportFilterState): void {
    this.filterState = filter;
    this.currentPage = 1;
    this.loadFinancialData();
  }

  onTransactionFilterChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  loadFinancialData(): void {
    this.loadCurrentBalance();
    this.loadBalanceHistory();
    this.loadTransactions();
  }

  private buildPeriodParams(): Record<string, any> {
    return toApiParams(this.filterState);
  }

  private buildTransactionParams(): Record<string, any> {
    const params: Record<string, any> = {
      ...this.buildPeriodParams(),
      page: this.currentPage,
      limit: this.pageSize
    };
    if (this.transactionType) params['transactionType'] = this.transactionType;
    if (this.paymentStatus) params['paymentStatus'] = this.paymentStatus;
    if (this.paymentMethod) params['paymentMethod'] = this.paymentMethod;
    return params;
  }

  loadCurrentBalance(): void {
    const seq = ++this.balanceLoadSeq;
    this.loadingBalance = true;
    this.ecommerceService.getCurrentBalance(this.storeId, this.buildPeriodParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.balanceLoadSeq) return;
        const data = this.unwrapResponse(res);
        this.currentBalance = data?.balance || data?.currentBalance || data;
        this.payoutEstimate = data?.payoutEstimate || null;
        this.loadingBalance = false;
      },
      error: (err: any) => {
        if (seq !== this.balanceLoadSeq) return;
        this.currentBalance = null;
        this.payoutEstimate = null;
        this.loadingBalance = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load current balance');
      }
    });
  }

  loadBalanceHistory(): void {
    const seq = ++this.balanceHistoryLoadSeq;
    this.loadingBalanceHistory = true;
    const year = new Date().getFullYear();

    this.ecommerceService.getBalanceHistory(this.storeId, year, 12).subscribe({
      next: (res: any) => {
        if (seq !== this.balanceHistoryLoadSeq) return;
        const data = this.unwrapResponse(res);
        this.balanceHistory = data?.balances || data?.history || [];
        this.loadingBalanceHistory = false;
      },
      error: (err: any) => {
        if (seq !== this.balanceHistoryLoadSeq) return;
        this.balanceHistory = [];
        this.loadingBalanceHistory = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load balance history');
      }
    });
  }

  loadTransactions(): void {
    const seq = ++this.transactionsLoadSeq;
    this.loadingTransactions = true;
    this.filteredTotals = null;
    this.ecommerceService.getStoreTransactions(this.storeId, this.buildTransactionParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.transactionsLoadSeq) return;
        const data = this.unwrapResponse(res);
        this.transactions = data?.transactions || [];
        this.filteredTotals = data?.filteredTotals || null;
        const pagination = data?.pagination || {};
        this.totalTransactions = pagination.totalTransactions || 0;
        this.totalPages = pagination.totalPages || 1;
        this.currentPage = pagination.currentPage || 1;
        this.loadingTransactions = false;
      },
      error: (err: any) => {
        if (seq !== this.transactionsLoadSeq) return;
        this.transactions = [];
        this.filteredTotals = null;
        this.loadingTransactions = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load transactions');
      }
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadTransactions();
  }

  onExport(event: { format: string }): void {
    this.spinner.show();
    const params: any = {
      format: event.format,
      ...this.buildPeriodParams()
    };
    if (this.transactionType) params.transactionType = this.transactionType;
    if (this.paymentStatus) params.paymentStatus = this.paymentStatus;
    if (this.paymentMethod) params.paymentMethod = this.paymentMethod;

    this.ecommerceService.exportTransactions(this.storeId, params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `transactions-${this.storeId}-${new Date().toISOString().split('T')[0]}.${event.format}`);
        this.spinner.hide();
        this.toastr.success(this.translate.instant('KPI.FINANCIAL.EXPORT_SUCCESS'));
      },
      error: (error: any) => {
        this.spinner.hide();
        this.handleBlobError(error, 'Failed to export transactions');
      }
    });
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  handleBlobError(error: any, fallback: string): void {
    if (error?.error instanceof Blob) {
      error.error.text().then((text: string) => {
        try {
          const parsed = JSON.parse(text);
          this.toastr.error(parsed?.response?.message || parsed?.message || fallback);
        } catch {
          this.toastr.error(text || fallback);
        }
      }).catch(() => this.toastr.error(fallback));
    } else {
      this.toastr.error(error?.error?.response?.message || error?.message || fallback);
    }
  }

  unwrapResponse(res: any): any {
    if (res?.response?.success && res.response.data !== undefined) return res.response.data;
    if (res?.code === 200 && res.data !== undefined) return res.data;
    return res;
  }

  formatCurrency(value: number): string {
    if (value == null) return 'kr 0.00';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(value);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  getMonthLabel(entry: any): string {
    if (!entry) return '-';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[(entry.month || 1) - 1]} ${entry.year || ''}`;
  }
}
